import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {
  MaterialInventoryWithPlanning,
  MaterialPlanningDate,
  MaterialPlanningDateWithColorCodes,
} from './material-planning.model';
import {MaterialValueType} from '../material-management.model';
import {Customer} from '../../customer/model/customer';

@Component({
    selector: 'tr[material-planning-row]',
    templateUrl: './material-planning-row.component.html',
    styleUrls: ['./material-planning.styles.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MaterialPlanningRowComponent implements OnInit, OnChanges {

  @Input('material-planning-row') materialChoice: MaterialInventoryWithPlanning;
  @Input('material-planning-row-week-pages') weekPages: number[];
  @Input('material-planning-row-selected-week') selectedWeek: MaterialPlanningDate | null;
  @Input('material-planning-row-selected-variation-id') selectedVariationId: number | null;
  @Input('material-planning-row-start-date') startDate: string | null;
  @Input('material-planning-row-fields') fields: MaterialValueType[] = [];
  @Input('material-planning-row-customer') customer: Customer;

  @Output('material-planning-row-select-week') emitSelectWeek =
    new EventEmitter<MaterialPlanningDate | null>();

  selectedVariation: boolean;

  constructor() {
  }

  ngOnInit(): void {
    this.selectedVariation = this.selectedVariationId === this.materialChoice.inventory.id;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedVariationId && !changes.selectedVariationId.isFirstChange()) {
      this.selectedVariation = this.selectedVariationId === this.materialChoice.inventory.id;
    }
  }

  setSelectedWeek(week: MaterialPlanningDate) {
    if (this.selectedWeek?.first_day_of_week === week.first_day_of_week && this.selectedVariation) {
      this.emitSelectWeek.emit(null);
    } else {
      this.emitSelectWeek.emit(week);
    }
  }

  get leadTimeWeeks(): number {
    const leadTimeDays = this.materialChoice.inventory.variation?.lead_time ?? 0;
    return Math.ceil(leadTimeDays / 5);
  }

  private get weeksInLeadTime(): MaterialPlanningDate[] {
    return this.materialChoice.workload_list.slice(0, this.leadTimeWeeks);
  }

  private isWeekMissed(week: MaterialPlanningDate): boolean {
    return week.required_to_date > week.secure_confirmed_to_date + this.materialChoice.inventory.amount;
  }

  get weeksWithColors(): MaterialPlanningDateWithColorCodes[] {
    const leadTimeWeeks = this.leadTimeWeeks;
    const markRed = this.markRed;
    return this.weekPages.map(w => {
      const c = {...this.materialChoice.workload_list[w]} as MaterialPlanningDateWithColorCodes;
      const variation = this.materialChoice.inventory.variation;
      const isWeekMissed = this.isWeekMissed(c);
      const inLeadTime = w < leadTimeWeeks;
      c.class_left = {};
      c.class_right = {};

      c.tooltip_description = `${variation.variation_id} ${variation.article.name} in KW ${c.week}`;
      if (markRed && inLeadTime) {
        c.tooltip_lead_time = 'Material kann eventuell nicht rechtzeitig beschafft werden';
      } else if (inLeadTime) {
        c.tooltip_lead_time = 'Beschaffungszeit';
      }

      if (c.first_day_of_week === this.selectedWeek?.first_day_of_week && this.selectedVariation) {
        c.color1 = 'light-blue';
        c.color2 = 'dark-blue';
      } else if (!isWeekMissed) {
        c.color1 = 'light-green';
        c.color2 = 'dark-green';
      } else {
        c.color1 = 'light-red';
        c.color2 = 'dark-red';
      }

      c.class_left[c.color1] = true;
      c.class_right[c.color2] = true;

      if (inLeadTime) {
        c.class_left['inset-shadow-b-10px'] = true;
        c.class_right['inset-shadow-b-10px'] = true;
        if (isWeekMissed) {
          c.class_left['inset-shadow-storz-red'] = true;
          c.class_right['inset-shadow-storz-red'] = true;
        } else {
          c.class_left['inset-shadow-storz-gray'] = true;
          c.class_right['inset-shadow-storz-gray'] = true;
        }
      }

      return c;
    });
  }

  get materialInventoryValue(): number {
    return (
      this.materialChoice.inventory.amount +
      this.materialChoice.inventory.amount_confirmed
    );
  }

  get materialToSelectedDateValue(): number | null | undefined {
    if (this.materialChoice.has_selected_date) {
      return (
        this.materialChoice.inventory.amount +
        this.materialChoice.total_confirmed_to_selected_date
      );
    } else {
      return null;
    }
  }

  get markOrange(): boolean {
    return this.materialChoice.inventory.amount_planned > this.materialInventoryValue;
  }

  get markRed(): boolean {
    return this.weeksInLeadTime.some(week => this.isWeekMissed(week));
  }

  get markOrangeToSelectedDate(): boolean {
    return this.materialChoice.has_selected_date && (
      this.materialChoice.total_required_to_selected_date > this.materialToSelectedDateValue
    );
  }

  get markRedCustomer(): boolean {
    return this.materialChoice.has_selected_customer && (
      this.materialChoice.total_required_customer > (this.materialToSelectedDateValue ?? this.materialInventoryValue)
    );
  }

  get linkParams() {
    return {
      variation_id: this.materialChoice.inventory.id,
      customer: this.customer?.name,
      start_date: this.startDate,
      start_date_mode: !this.startDate ? null : '<=',
      status: 0,
    };
  }

}
